import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Waves, Gauge, Timer, CheckCircle2, AlertCircle,
  Info, Save, RotateCcw, Microscope, BarChart3, Zap,
  Thermometer, ArrowDown, ArrowRightLeft, Droplets,
  Factory, Milk, FishSymbol, ShieldAlert, Database,
  FlaskConical, Settings, Beaker, ArrowRight, Filter
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

interface SecondaryParams {
  flowRate: number;
  influentBOD: number;
  influentCOD: number;
  influentTSS: number;
  influentMLSS: number;
  temperature: number;
  og: number; ph: number; alkalinity: number; turbidity: number;
  conductivity: number; hardness: number; nh3n: number; tp: number;
  tankDiameter: number;
  tankDepth: number;
  freeboard: number;
  scraperSpeed: number;
  rasPercent: number;
  svi: number;
  targetDS: number;
}

interface SecondarySize {
  tankArea: number;
  tankVolume: number;
  actualSOR: number;
  actualSLR: number;
  actualDetention: number;
  rasFlow: number;
  totalFlow: number;
  settlingVelocity: number;
  zoneSettlingVelocity: number;
  sviFactor: number;
  removalEfficiency: number;
  effluentTSS: number;
  sludgeVolume: number;
  sludgeDryMass: number;
  bulkingRisk: string;
  solidsFluxRisk: string;
  scraperPower: number;
  scraperTorque: number;
  weirLoadingRate: number;
  status: 'optimal' | 'acceptable' | 'undersized' | 'oversized' | 'overloaded';
  statusDetail: string;
  designNotes: string[];
}

const PROFILES = [
  { name: 'Dairy Effluent', description: 'Good settling sludge, moderate MLSS', icon: Milk,
    defaults: { flowRate: 250, influentBOD: 250, influentCOD: 450, influentTSS: 120, influentMLSS: 3500, temperature: 25,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      tankDiameter: 12, tankDepth: 3.5, freeboard: 0.5, scraperSpeed: 1.5, rasPercent: 50, svi: 100, targetDS: 3 } },
  { name: 'Brewery Effluent', description: 'Poor settling, high SVI', icon: Factory,
    defaults: { flowRate: 350, influentBOD: 320, influentCOD: 580, influentTSS: 150, influentMLSS: 3000, temperature: 20,
      og: 180, ph: 5.2, alkalinity: 80, turbidity: 550, conductivity: 4200, hardness: 350, nh3n: 85, tp: 18,
      tankDiameter: 15, tankDepth: 4, freeboard: 0.5, scraperSpeed: 2, rasPercent: 60, svi: 180, targetDS: 3 } },
  { name: 'Fish Processing', description: 'Good settling, low SVI', icon: FishSymbol,
    defaults: { flowRate: 150, influentBOD: 180, influentCOD: 340, influentTSS: 90, influentMLSS: 2800, temperature: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      tankDiameter: 10, tankDepth: 3, freeboard: 0.5, scraperSpeed: 1.5, rasPercent: 40, svi: 80, targetDS: 3 } },
];

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

export function calcSecondary(p: SecondaryParams): SecondarySize {
  const Q = p.flowRate;
  const q = Q / 24;
  const radius = p.tankDiameter / 2;
  const area = Math.PI * radius * radius;
  const volume = area * p.tankDepth;
  const detention = volume / q;

  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
  const rhoWater = 998 - 0.2 * (p.temperature - 20);

  const sor = Q / area;
  const mlssMass = (Q * p.influentMLSS) / 1000;
  const slr = mlssMass / area;

  // Vesilind settling
  const v0 = 10;
  const kVesilind = 0.45;
  const settlingVelocity = v0 * Math.exp(-kVesilind * p.influentMLSS / 1000);
  const sviFactor = p.svi < 80 ? 1.2 : p.svi < 120 ? 1.0 : p.svi < 200 ? 0.8 : 0.6;
  const zoneSettlingVelocity = settlingVelocity * sviFactor;

  const weirLength = Math.PI * p.tankDiameter;
  const weirLoadingRate = q / weirLength;

  const returnFlow = (Q * p.rasPercent) / 100;
  const totalFlow = Q + returnFlow;

  const solidsIn = (Q * p.influentTSS) / 1000;
  const removalEfficiency = Math.min(0.95, 0.70 + (sviFactor - 0.6) * 0.3 - Math.max(0, (sor - 20) * 0.01));
  const effluentTSS = p.influentTSS * (1 - removalEfficiency);
  const solidsOut = solidsIn * removalEfficiency;

  const sludgeDryMass = solidsOut;
  const sludgeVolume = sludgeDryMass / (p.targetDS * 10);

  const scraperRadius = radius * 0.9;
  const scraperForce = 50 * p.influentMLSS / 1000;
  const scraperTorque = scraperForce * area * scraperRadius;
  const scraperPower = (scraperTorque * p.scraperSpeed * 2 * Math.PI) / (60 * 1000);

  let status: SecondarySize['status']; let statusDetail: string;
  const designNotes: string[] = [];

  if (sor <= 20 && slr <= 150 && p.svi <= 150 && removalEfficiency >= 0.80) {
    status = 'optimal';
    statusDetail = `Optimal. SOR ${sor.toFixed(1)} m³/m²·d, SVI ${p.svi}, removal ${(removalEfficiency*100).toFixed(0)}%.`;
  } else if (sor <= 35 && slr <= 250 && p.svi <= 200 && removalEfficiency >= 0.65) {
    status = 'acceptable';
    statusDetail = `Acceptable. SOR ${sor.toFixed(1)} m³/m²·d. Monitor SVI.`;
  } else if (sor > 50 || slr > 400 || p.svi > 250 || removalEfficiency < 0.50) {
    status = 'overloaded';
    statusDetail = `Overloaded. SOR ${sor.toFixed(1)} or SVI ${p.svi} too high.`;
  } else if (volume < 50) {
    status = 'undersized';
    statusDetail = `Undersized. Volume ${volume.toFixed(0)} m³. Increase diameter.`;
  } else {
    status = 'oversized';
    statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  if (p.svi > 200) designNotes.push('⚠️ High SVI — sludge bulking risk. Check DO and F/M ratio.');
  if (slr > 250) designNotes.push('⚠️ Solid loading rate too high. Increase clarifier area or reduce MLSS.');
  if (sor > 35) designNotes.push('⚠️ Surface overflow rate high. Increase diameter or add parallel clarifiers.');
  if (removalEfficiency < 0.70) designNotes.push('⚠️ Low removal efficiency. Check sludge volume index and recycle ratio.');
  if (designNotes.length === 0) designNotes.push('✅ All design parameters within recommended ranges.');

  return {
    tankArea: area, tankVolume: volume,
    actualSOR: sor, actualSLR: slr, actualDetention: detention,
    rasFlow: returnFlow, totalFlow,
    settlingVelocity, zoneSettlingVelocity, sviFactor,
    removalEfficiency, effluentTSS,
    sludgeVolume, sludgeDryMass,
    bulkingRisk: p.svi > 200 ? 'high' : p.svi > 150 ? 'moderate' : 'low',
    solidsFluxRisk: slr > 250 ? 'high' : slr > 150 ? 'moderate' : 'low',
    weirLoadingRate, scraperPower, scraperTorque,
    status, statusDetail, designNotes,
  };
}

function S({ label, value, min, max, step, unit, icon: Icon, color = '#06b6d4', onChange, info }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : min;
  const pct = Math.min(100, Math.max(0, ((safeValue - min) / (max - min)) * 100));
  const displayVal = step < 0.1 ? safeValue.toFixed(2) : step < 1 ? safeValue.toFixed(1) : Math.round(safeValue).toString();
  return (
    <div className="space-y-1.5 group/slider">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a` }}>
            <Icon className="w-3 h-3 shrink-0" style={{ color }} />
          </div>
          <span className="text-xs font-medium text-slate-300 truncate">{label}</span>
          {info && (
            <div className="group/tip relative shrink-0">
              <Info className="w-3 h-3 text-slate-400 cursor-help hover:text-slate-400 transition-colors" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 p-2.5 bg-[#080e1a] border border-slate-700/80 rounded-xl text-[10px] text-slate-400 opacity-0 group-hover/tip:opacity-100 transition-all pointer-events-none z-50 shadow-2xl leading-relaxed">{info}</div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs font-bold text-white tabular-nums">{displayVal}</span>
          <span className="text-[10px] text-slate-600 font-normal">{unit}</span>
        </div>
      </div>
      <div className="relative h-5 flex items-center">
        <div className="absolute inset-x-0 h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-75" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}55, ${color}dd)` }} />
        </div>
        <div
          className="absolute w-3.5 h-3.5 rounded-full bg-rb-surface shadow-lg -translate-x-1/2 transition-transform duration-75 group-hover/slider:scale-110 pointer-events-none"
          style={{ left: `${pct}%`, border: `2px solid ${color}`, boxShadow: `0 0 0 2px ${color}22, 0 2px 6px rgba(0,0,0,0.5)` }}
        />
        <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(e) => onChange(parseFloat(e.target.value))} className="rb-slider absolute inset-x-0 opacity-0 h-5" style={{ color }} />
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 select-none">
        <span>{min}</span>
        <span>{max}{unit && unit.length < 6 ? ` ${unit}` : ''}</span>
      </div>
    </div>
  );
}

function G({ value, max, label, unit, color }: any) {
  const safeValue = typeof value === 'number' && !isNaN(value) ? value : 0;
  const safeMax = typeof max === 'number' && max > 0 ? max : 1;
  const pct = Math.min(100, (safeValue / safeMax) * 100);
  const displayVal = safeValue < 10 ? safeValue.toFixed(1) : safeValue.toFixed(0);
  return (
    <div className="rb-metric-card p-2.5 space-y-1.5">
      <div className="flex justify-between items-baseline gap-1">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold leading-tight">{label}</span>
        <span className="text-xs font-bold tabular-nums shrink-0" style={{ color }}>{displayVal}<span className="text-[10px] font-normal text-slate-600 ml-0.5">{unit}</span></span>
      </div>
      <div className="h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }} className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${color}55, ${color})` }} />
      </div>
      <div className="text-right text-[10px] font-medium tabular-nums" style={{ color: `${color}99` }}>{pct.toFixed(0)}%</div>
    </div>
  );
}

function PC({ profile, isActive, onClick }: any) {
  const Icon = profile.icon;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
        isActive
          ? 'bg-gradient-to-br from-amber-500/15 to-amber-600/5 border-amber-500/40 rb-profile-active'
          : 'bg-rb-elevated/60 border-slate-800/80 hover:border-slate-700 hover:bg-rb-elevated'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all ${isActive ? 'bg-amber-500/20 shadow-inner shadow-amber-900/30' : 'bg-slate-800'}`}>
          <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-amber-400' : 'text-slate-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-bold truncate transition-colors ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p>
          <p className="text-[10px] text-slate-500 leading-tight mt-0.5 truncate">{profile.description}</p>
        </div>
        {isActive && (
          <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-3 h-3 text-amber-400" />
          </div>
        )}
      </div>
    </button>
  );
}

function FlowDiagram({ params, size }: { params: SecondaryParams; size: SecondarySize }) {
  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowRightLeft className="w-3 h-3" /> Process Flow — MLSS → Clarifier → Effluent + RAS
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs><linearGradient id="waterGradS" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" /><stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" /></linearGradient></defs>
        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">SECONDARY CLARIFIER — VESILIND SETTLING + SOLIDS FLUX</text>

        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradS)" stroke="#a855f7" strokeWidth="2" />
        <text x="80" y="72" fill="#a855f7" fontSize="9" textAnchor="middle" fontWeight="bold">Mixed Liquor</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">MLSS {fmt(params.influentMLSS, 0)} mg/L</text>
        <text x="80" y="100" fill="#a855f7" fontSize="8" textAnchor="middle">Q {fmt(params.flowRate, 0)} m³/d</text>
        <text x="80" y="112" fill="#94a3b8" fontSize="8" textAnchor="middle">SVI {fmt(params.svi, 0)} mL/g</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#a855f7" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#a855f7" />

        {/* Clarifier */}
        <rect x="180" y="40" width="280" height="110" rx="6" fill="url(#waterGradS)" stroke="#06b6d4" strokeWidth="2" />
        <text x="320" y="58" fill="#06b6d4" fontSize="10" textAnchor="middle" fontWeight="bold">Secondary Clarifier</text>
        <text x="320" y="74" fill="#94a3b8" fontSize="8" textAnchor="middle">Ø {fmt(params.tankDiameter, 1)} m × {fmt(params.tankDepth, 1)} m | Vol {fmt(size.tankVolume, 0)} m³</text>
        <text x="320" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">SOR {fmt(size.actualSOR, 1)} m³/m²·d | SLR {fmt(size.actualSLR, 1)} kg/m²·d</text>
        <text x="320" y="102" fill="#f59e0b" fontSize="8" textAnchor="middle">Settling {fmt(size.settlingVelocity, 1)} m/h | Zone {fmt(size.zoneSettlingVelocity, 1)} m/h</text>
        <text x="320" y="116" fill="#94a3b8" fontSize="8" textAnchor="middle">Weir {fmt(size.weirLoadingRate, 1)} m³/m·h | Scraper {fmt(params.scraperSpeed, 1)} rpm</text>

        {/* Scraper */}
        <motion.g animate={{ x: [0, 30, 0] }} transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}>
          <line x1="200" y1="48" x2="250" y2="48" stroke="#64748b" strokeWidth="2" />
        </motion.g>

        {/* Sludge blanket */}
        <rect x="185" y="125" width="270" height="20" rx="2" fill="#8B4513" opacity="0.3" />
        <text x="320" y="138" fill="#8B4513" fontSize="7" textAnchor="middle">Sludge blanket | {fmt(size.sludgeDryMass, 1)} kg/d</text>

        <line x1="462" y1="75" x2="510" y2="75" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="508,72 515,75 508,78" fill="#10b981" />

        <rect x="520" y="55" width="100" height="80" rx="6" fill="url(#waterGradS)" stroke="#10b981" strokeWidth="2" />
        <text x="570" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="570" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS {fmt(size.effluentTSS, 0)} mg/L</text>
        <text x="570" y="100" fill="#10b981" fontSize="8" textAnchor="middle">Removal {(size.removalEfficiency * 100).toFixed(0)}%</text>

        {/* RAS */}
        <line x1="320" y1="150" x2="320" y2="200" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="320" y1="200" x2="80" y2="200" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="80" y1="200" x2="80" y2="135" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4 2" />
        <polygon points="77,137 80,130 83,137" fill="#f59e0b" />
        <text x="200" y="210" fill="#f59e0b" fontSize="7" textAnchor="middle">RAS {fmt(params.rasPercent, 0)}% = {fmt(size.rasFlow, 0)} m³/d</text>

        {/* Sludge pump */}
        <line x1="320" y1="150" x2="320" y2="240" stroke="#8B4513" strokeWidth="1.5" strokeDasharray="4 2" />
        <line x1="320" y1="240" x2="260" y2="240" stroke="#8B4513" strokeWidth="1.5" strokeDasharray="4 2" />
        <rect x="220" y="230" width="35" height="20" rx="3" fill="#0f172a" stroke="#8B4513" strokeWidth="1" />
        <text x="237" y="243" fill="#8B4513" fontSize="7" textAnchor="middle">WAS</text>

        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#a855f7" fontSize="9">— Mixed Liquor</text>
        <text x="140" y="359" fill="#06b6d4" fontSize="9">— Clarifier</text>
        <text x="215" y="359" fill="#10b981" fontSize="9">— Effluent</text>
        <text x="280" y="359" fill="#f59e0b" fontSize="9">— RAS</text>
        <text x="330" y="359" fill="#8B4513" fontSize="9">— Waste Sludge</text>
        <text x="440" y="359" fill="#94a3b8" fontSize="9">SOR={fmt(size.actualSOR, 1)} | SLR={fmt(size.actualSLR, 1)} | SVI={fmt(params.svi, 0)}</text>
      </svg>
    </div>
  );
}

export default function SecondarySpecPage() {
  const [params, setParams] = useState<SecondaryParams>(PROFILES[0].defaults);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'process'>('influent');
  const [profileIndex, setProfileIndex] = useState(0);

  const size = useMemo(() => calcSecondary(params), [params]);
  const sampleLoader = useSampleLoader<SecondaryParams>(setParams);

  const handleReset = () => { setParams(PROFILES[0].defaults); setProfileIndex(0); sampleLoader.clearSample(); toast.info('Parameters reset to defaults'); };
  const handleSave = () => { localStorage.setItem('secondary-params-v2', JSON.stringify(params)); setSaved(true); setTimeout(() => setSaved(false), 2000); toast.success('Configuration saved'); };

  useEffect(() => {
    const savedParams = localStorage.getItem('secondary-params-v2');
    if (savedParams) { try { setParams(JSON.parse(savedParams)); } catch {} }
  }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const Q = params.flowRate;
    const q = Q / 24;
    const radius = params.tankDiameter / 2;
    const area = Math.PI * radius * radius;
    const volume = area * params.tankDepth;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
    const rhoWater = 998 - 0.2 * (params.temperature - 20);
    const sor = Q / area;
    const mlssMass = (Q * params.influentMLSS) / 1000;
    const slr = mlssMass / area;
    const v0 = 10;
    const kVesilind = 0.45;
    const settlingVelocity = v0 * Math.exp(-kVesilind * params.influentMLSS / 1000);
    const sviFactor = params.svi < 80 ? 1.2 : params.svi < 120 ? 1.0 : params.svi < 200 ? 0.8 : 0.6;
    const zoneSettlingVelocity = settlingVelocity * sviFactor;
    const removalEfficiency = Math.min(0.95, 0.70 + (sviFactor - 0.6) * 0.3 - Math.max(0, (sor - 20) * 0.01));
    const effluentTSS = params.influentTSS * (1 - removalEfficiency);
    const solidsIn = (Q * params.influentTSS) / 1000;
    const sludgeDryMass = solidsIn * removalEfficiency;
    const sludgeVolume = sludgeDryMass / (params.targetDS * 10);
    const scraperRadius = radius * 0.9;
    const scraperForce = 50 * params.influentMLSS / 1000;
    const scraperTorque = scraperForce * area * scraperRadius;
    const scraperPower = (scraperTorque * params.scraperSpeed * 2 * Math.PI) / (60 * 1000);
    return [
      {
        title: 'Settling Physics — Vesilind & SVI',
        icon: Droplets,
        equations: [
          { label: 'Flow rate (hourly)', formula: 'q = flowRate / 24', substitution: `q = ${sharedFmt(Q, 0)} / 24 = ${sharedFmt(q, 1)} m³/h`, result: sharedFmt(q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Tank area', formula: 'A = π × (D/2)²', substitution: `A = π × (${params.tankDiameter}/2)² = ${sharedFmt(area, 1)} m²`, result: sharedFmt(area, 1), status: 'pass', unit: 'm²' },
          { label: 'Tank volume', formula: 'V = A × depth', substitution: `V = ${sharedFmt(area, 1)} × ${params.tankDepth} = ${sharedFmt(volume, 1)} m³`, result: sharedFmt(volume, 1), status: 'pass', unit: 'm³' },
          { label: 'Water viscosity', formula: 'μ = 0.001 × 10^(247.8/T_K − 140)', substitution: `μ = 0.001 × 10^(247.8/${sharedFmt(tempK, 1)} − 140) = ${sharedFmt(mu * 1000, 3)} mPa·s`, result: sharedFmt(mu * 1000, 3), status: 'pass', unit: 'mPa·s' },
          { label: 'Vesilind settling', formula: 'v = v₀ × exp(−k × MLSS/1000)', substitution: `v = 10 × exp(−0.45 × ${params.influentMLSS}/1000) = ${sharedFmt(settlingVelocity, 2)} m/h`, result: sharedFmt(settlingVelocity, 2), status: 'pass', unit: 'm/h' },
          { label: 'SVI factor', formula: 'f = SVI<80?1.2 : <120?1.0 : <200?0.8 : 0.6', substitution: `f = ${sharedFmt(sviFactor, 1)}`, result: sharedFmt(sviFactor, 1), status: sviFactor >= 0.8 ? 'pass' : 'warn', unit: '' },
          { label: 'Zone settling velocity', formula: 'v_z = v × f', substitution: `v_z = ${sharedFmt(settlingVelocity, 2)} × ${sharedFmt(sviFactor, 1)} = ${sharedFmt(zoneSettlingVelocity, 2)} m/h`, result: sharedFmt(zoneSettlingVelocity, 2), status: zoneSettlingVelocity > 2 ? 'pass' : 'warn', unit: 'm/h' },
        ],
      },
      {
        title: 'Solids Loading & Removal',
        icon: Filter,
        equations: [
          { label: 'Surface overflow rate (SOR)', formula: 'SOR = Q / A', substitution: `SOR = ${sharedFmt(Q, 0)} / ${sharedFmt(area, 1)} = ${sharedFmt(sor, 1)} m³/m²·d`, result: sharedFmt(sor, 1), status: sor <= 20 ? 'pass' : sor <= 35 ? 'warn' : 'fail', unit: 'm³/m²·d' },
          { label: 'Solids loading rate (SLR)', formula: 'SLR = (Q × MLSS/1000) / A', substitution: `SLR = (${sharedFmt(Q, 0)} × ${params.influentMLSS}/1000) / ${sharedFmt(area, 1)} = ${sharedFmt(slr, 1)} kg/m²·d`, result: sharedFmt(slr, 1), status: slr <= 150 ? 'pass' : slr <= 250 ? 'warn' : 'fail', unit: 'kg/m²·d' },
          { label: 'Removal efficiency', formula: 'η = min(0.95, 0.70 + (f−0.6)×0.3 − max(0, (SOR−20)×0.01))', substitution: `η = min(0.95, 0.70 + (${sharedFmt(sviFactor, 1)}−0.6)×0.3 − max(0, (${sharedFmt(sor, 1)}−20)×0.01)) = ${sharedFmt(removalEfficiency * 100, 0)}%`, result: sharedFmt(removalEfficiency * 100, 0), status: removalEfficiency >= 0.80 ? 'pass' : removalEfficiency >= 0.65 ? 'warn' : 'fail', unit: '%' },
          { label: 'Effluent TSS', formula: 'TSS_eff = TSS_in × (1 − η)', substitution: `TSS_eff = ${params.influentTSS} × (1 − ${sharedFmt(removalEfficiency, 2)}) = ${sharedFmt(effluentTSS, 0)} mg/L`, result: sharedFmt(effluentTSS, 0), status: effluentTSS <= 30 ? 'pass' : effluentTSS <= 60 ? 'warn' : 'fail', unit: 'mg/L' },
        ],
      },
      {
        title: 'Sludge & Power',
        icon: Zap,
        equations: [
          { label: 'Solids in', formula: 'm_in = Q × TSS / 1000', substitution: `m_in = ${sharedFmt(Q, 0)} × ${params.influentTSS} / 1000 = ${sharedFmt(solidsIn, 1)} kg/d`, result: sharedFmt(solidsIn, 1), status: 'pass', unit: 'kg/d' },
          { label: 'Sludge dry mass', formula: 'm_sludge = m_in × η', substitution: `m_sludge = ${sharedFmt(solidsIn, 1)} × ${sharedFmt(removalEfficiency, 2)} = ${sharedFmt(sludgeDryMass, 1)} kg/d`, result: sharedFmt(sludgeDryMass, 1), status: 'pass', unit: 'kg/d' },
          { label: 'Sludge volume', formula: 'V = m_sludge / (DS × 10)', substitution: `V = ${sharedFmt(sludgeDryMass, 1)} / (${params.targetDS} × 10) = ${sharedFmt(sludgeVolume, 1)} m³/d`, result: sharedFmt(sludgeVolume, 1), status: 'pass', unit: 'm³/d' },
          { label: 'Scraper torque', formula: 'T = F × A × r', substitution: `T = ${sharedFmt(scraperForce, 0)} × ${sharedFmt(area, 1)} × ${sharedFmt(scraperRadius, 1)} = ${sharedFmt(scraperTorque, 0)} Nm`, result: sharedFmt(scraperTorque, 0), status: 'pass', unit: 'Nm' },
          { label: 'Scraper power', formula: 'P = (T × rpm × 2π) / (60 × 1000)', substitution: `P = (${sharedFmt(scraperTorque, 0)} × ${params.scraperSpeed} × 2π) / 60000 = ${sharedFmt(scraperPower, 2)} kW`, result: sharedFmt(scraperPower, 2), status: scraperPower <= 3 ? 'pass' : scraperPower <= 5 ? 'warn' : 'fail', unit: 'kW' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    undersized: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    overloaded: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };
  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Droplets}
        iconColor="text-emerald-400"
        title="Secondary Clarifier Specification"
        subtitle="Vesilind settling kinetics · SVI solids flux · Scraper power & torque · RAS recycle control"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=secondary')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400 text-xs font-bold hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      {/* ═════ STATUS BANNER ═════ */}
      <motion.div layout className={`rounded-xl border p-4 ${sc.bg} ${sc.border} rb-status-${size.status}`}>
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${sc.bg} border ${sc.border}`}>
            <StatusIcon className={`w-6 h-6 ${sc.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-sm font-bold tracking-wider uppercase ${sc.text}`}>{size.status}</p>
              <div className={`w-1.5 h-1.5 rounded-full ${sc.text.replace('text-', 'bg-')} rb-pulse-dot`} />
            </div>
            <p className={`text-xs mt-0.5 leading-relaxed ${sc.text} opacity-75`}>{size.statusDetail}</p>
          </div>
          <div className="hidden md:flex items-center gap-4 text-xs shrink-0">
            <div className="text-center">
              <p className="text-slate-500 text-[10px] uppercase tracking-wider">SOR</p>
              <p className="font-bold text-white">{fmt(size.actualSOR, 1)} <span className="text-slate-500 font-normal text-[10px]">m³/m²·d</span></p>
            </div>
            <div className="text-center">
              <p className="text-slate-500 text-[10px] uppercase tracking-wider">SLR</p>
              <p className="font-bold text-sky-400">{fmt(size.actualSLR, 1)} <span className="text-slate-500 font-normal text-[10px]">kg/m²·d</span></p>
            </div>
            <div className="text-center">
              <p className="text-slate-500 text-[10px] uppercase tracking-wider">TSS Removal</p>
              <p className="font-bold text-emerald-400">{(size.removalEfficiency * 100).toFixed(0)}%</p>
            </div>
            <div className="text-center">
              <p className="text-slate-500 text-[10px] uppercase tracking-wider">Scraper</p>
              <p className="font-bold text-amber-400">{fmt(size.scraperPower, 2)} <span className="text-slate-500 font-normal text-[10px]">kW</span></p>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PROFILES.map((profile, i) => (
          <PC key={i} profile={profile} isActive={profileIndex === i} onClick={() => { setParams(profile.defaults); setProfileIndex(i); toast.info(`Loaded ${profile.name} profile`); }} />
        ))}
      </div>

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'BOD', value: `${sharedFmt(params.influentBOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.bod || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
              <Microscope className="w-3.5 h-3.5 text-emerald-400" />Vesilind Settling Flow Diagram
            </h3>
            <button onClick={() => setShowFlow(!showFlow)} className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-300 px-2.5 py-1.5 rounded-lg hover:bg-slate-800/50 transition-all">
              <Settings className="w-3 h-3" />{showFlow ? 'Collapse ▲' : 'Expand ▼'}
            </button>
          </div>
          <AnimatePresence>{showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}</AnimatePresence>
        </div>

        <div className="xl:col-span-5 space-y-4">
          <div className="p-4 rb-card">
            <h3 className="text-xs font-bold text-slate-300 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Gauge className="w-3.5 h-3.5 text-emerald-400" />Performance Gauges
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-2">
              <G value={size.removalEfficiency * 100} max={100} label="TSS Removal" unit="%" color="#10b981" />
              <G value={size.actualSOR} max={50} label="SOR" unit="m³/m²·d" color="#06b6d4" />
              <G value={size.actualSLR} max={300} label="SLR" unit="kg/m²·d" color="#f59e0b" />
              <G value={size.settlingVelocity} max={20} label="Settling" unit="m/h" color="#ef4444" />
              <G value={size.sviFactor} max={1.5} label="SVI Factor" unit="" color="#a855f7" />
              <G value={size.scraperPower} max={5} label="Scraper" unit="kW" color="#f97316" />
              <G value={size.rasFlow} max={500} label="RAS Flow" unit="m³/d" color="#22c55e" />
              <G value={size.effluentTSS} max={100} label="Effluent TSS" unit="mg/L" color="#8b5cf6" />
            </div>
          </div>

          <div className="overflow-hidden rb-card">
            <div className="flex border-b border-slate-800/80">
              {([
                { id: 'influent', label: 'Influent', icon: Droplets },
                { id: 'tank', label: 'Tank Design', icon: ArrowDown },
                { id: 'process', label: 'Process', icon: Zap },
              ] as const).map(({ id, label, icon: TabIcon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-[10px] font-bold uppercase tracking-wider transition-all relative ${
                    activeTab === id ? 'text-emerald-400 bg-emerald-500/5' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/30'
                  }`}
                >
                  <TabIcon className="w-3 h-3 shrink-0" />
                  {label}
                  {activeTab === id && (
                    <motion.div layoutId="sec-tab-indicator" className="absolute bottom-0 inset-x-0 h-0.5 bg-emerald-400" style={{ boxShadow: '0 0 8px rgba(16,185,129,0.6)' }} />
                  )}
                </button>
              ))}
            </div>
            <div className="p-4 space-y-4">
              {activeTab === 'influent' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Flow Rate" value={params.flowRate} min={24} max={2400} step={12} unit="m³/day" icon={Waves} color="#06b6d4" onChange={(v: number) => setParams({ ...params, flowRate: v })} />
                  <S label="Influent BOD" value={params.influentBOD} min={50} max={1000} step={10} unit="mg/L" icon={FlaskConical} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentBOD: v })} />
                  <S label="Influent COD" value={params.influentCOD} min={100} max={1500} step={20} unit="mg/L" icon={Beaker} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentCOD: v })} />
                  <S label="Influent TSS" value={params.influentTSS} min={20} max={500} step={10} unit="mg/L" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, influentTSS: v })} />
                  <S label="Influent MLSS" value={params.influentMLSS} min={1000} max={5000} step={100} unit="mg/L" icon={Droplets} color="#a855f7" onChange={(v: number) => setParams({ ...params, influentMLSS: v })} />
                  <S label="Temperature" value={params.temperature} min={5} max={35} step={1} unit="°C" icon={Thermometer} color="#ef4444" onChange={(v: number) => setParams({ ...params, temperature: v })} />
                  <S label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v: number) => setParams({ ...params, og: v })} />
                  <S label="Influent pH" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v: number) => setParams({ ...params, ph: v })} />
                  <S label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v: number) => setParams({ ...params, alkalinity: v })} />
                  <S label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v: number) => setParams({ ...params, turbidity: v })} />
                  <S label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v: number) => setParams({ ...params, conductivity: v })} />
                  <S label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v: number) => setParams({ ...params, hardness: v })} />
                  <S label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, nh3n: v })} />
                  <S label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v: number) => setParams({ ...params, tp: v })} />
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="Tank Diameter" value={params.tankDiameter} min={5} max={25} step={0.5} unit="m" icon={ArrowRight} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankDiameter: v })} />
                  <S label="Tank Depth" value={params.tankDepth} min={2} max={6} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, tankDepth: v })} />
                  <S label="Freeboard" value={params.freeboard} min={0.3} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#06b6d4" onChange={(v: number) => setParams({ ...params, freeboard: v })} />
                  <S label="Scraper Speed" value={params.scraperSpeed} min={0.5} max={4} step={0.1} unit="rpm" icon={Zap} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, scraperSpeed: v })} />
                </div>
              )}
              {activeTab === 'process' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <S label="RAS Percent" value={params.rasPercent} min={10} max={100} step={5} unit="%" icon={ArrowRightLeft} color="#f59e0b" onChange={(v: number) => setParams({ ...params, rasPercent: v })} />
                  <S label="SVI" value={params.svi} min={50} max={300} step={5} unit="mL/g" icon={Droplets} color="#a855f7" onChange={(v: number) => setParams({ ...params, svi: v })} />
                  <S label="Target DS" value={params.targetDS} min={1} max={6} step={0.1} unit="%" icon={Gauge} color="#8b5cf6" onChange={(v: number) => setParams({ ...params, targetDS: v })} />
                </div>
              )}
            </div>
          </div>

          <div className="p-4 space-y-4 rb-card">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
              <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />Results & Performance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-gradient-to-br from-emerald-950/40 to-rb-elevated rounded-xl p-3 border border-emerald-900/30 space-y-2">
                <p className="text-[10px] text-emerald-600 uppercase font-bold tracking-wider">Effluent Quality</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">TSS</span><span className="text-white font-bold">{fmt(params.influentTSS, 0)} → {fmt(size.effluentTSS, 0)} mg/L</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Removal</span><span className="text-emerald-400 font-bold">{(size.removalEfficiency * 100).toFixed(0)}%</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">BOD</span><span className="text-white font-bold">{fmt(params.influentBOD, 0)} → {fmt(params.influentBOD * (1 - size.removalEfficiency), 0)} mg/L</span></div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-sky-950/30 to-rb-elevated rounded-xl p-3 border border-sky-900/20 space-y-2">
                <p className="text-[10px] text-sky-600 uppercase font-bold tracking-wider">Solids & Settling</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">SOR</span><span className="text-sky-400 font-bold">{fmt(size.actualSOR, 1)} m³/m²·d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">SLR</span><span className="text-sky-400 font-bold">{fmt(size.actualSLR, 1)} kg/m²·d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Settling v</span><span className="text-sky-400 font-bold">{fmt(size.settlingVelocity, 1)} m/h</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">SVI factor</span><span className="text-amber-400 font-bold">{fmt(size.sviFactor, 2)}</span></div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-amber-950/30 to-rb-elevated rounded-xl p-3 border border-amber-900/20 space-y-2">
                <p className="text-[10px] text-amber-600 uppercase font-bold tracking-wider">Sludge & Power</p>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-400">Sludge mass</span><span className="text-amber-400 font-bold">{fmt(size.sludgeDryMass, 1)} kg/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Wet volume</span><span className="text-amber-400 font-bold">{fmt(size.sludgeVolume, 1)} m³/d</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Scraper power</span><span className="text-sky-400 font-bold">{fmt(size.scraperPower, 2)} kW</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Torque</span><span className="text-sky-400 font-bold">{fmt(size.scraperTorque, 0)} Nm</span></div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
              <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />Design Notes & Warnings
            </h3>
            <div className="space-y-1.5">
              {size.designNotes.map((note, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`text-xs p-2.5 rounded-lg leading-relaxed ${
                    note.startsWith('⚠️')
                      ? 'bg-rose-500/5 border border-rose-500/20 text-rose-400'
                      : note.startsWith('ℹ️')
                      ? 'bg-sky-500/5 border border-sky-500/20 text-sky-400'
                      : 'bg-emerald-500/5 border border-emerald-500/20 text-emerald-400'
                  }`}
                >
                  {note}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-xs font-bold text-slate-300 flex items-center gap-2 uppercase tracking-wider">
              <Microscope className="w-3.5 h-3.5 text-sky-400" />Science Validation
              <span className="ml-1 text-[10px] text-slate-600 normal-case tracking-normal font-normal">— click to expand equations</span>
            </h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
